import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

class CartMilestoneRewards extends HTMLElement {
  #observer = new MutationObserver(() => {
    this.#celebrateNewGoals();
    this.#queueSync();
  });
  #syncQueued = false;
  #queuedCartItems = null;

  connectedCallback() {
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-reached', 'data-reward-type', 'data-gift-variant-id'],
    });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartLinesUpdate);
    this.#celebrateNewGoals();
    this.#queueSync();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartLinesUpdate);
  }

  #handleCartLinesUpdate = (event) => {
    const currentRewards = document.querySelector('cart-milestone-rewards');
    if (currentRewards instanceof CartMilestoneRewards) {
      currentRewards.#queueSync(event.detail?.resource?.items);
    }

    // Cart sections morph after the update event finishes. Check the newly-rendered
    // markers in a microtask so a threshold crossed by this update always celebrates.
    queueMicrotask(() => {
      const updatedRewards = document.querySelector('cart-milestone-rewards');
      if (updatedRewards instanceof CartMilestoneRewards) updatedRewards.#celebrateNewGoals();
    });
  };

  #celebrateNewGoals() {
    const reachedLevels = new Set(
      [...this.querySelectorAll('[data-cart-milestone-reward][data-reached="true"]')].map(
        (reward) => reward.dataset.level
      )
    );
    const previousLevels = window.__cartMilestoneReachedLevels;
    window.__cartMilestoneReachedLevels = reachedLevels;

    // Establish the initial state without celebrating goals that were already reached before page load.
    if (!(previousLevels instanceof Set)) return;
    const newlyReached = [...reachedLevels].filter((level) => !previousLevels.has(level));
    if (!newlyReached.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.celebrate();
  }

  celebrate() {
    document.querySelector('.cart-milestone-confetti')?.remove();

    const colors = ['#211a15', '#c6922f', '#f1c75b', '#18bca8', '#ffffff'];
    const burst = document.createElement('div');
    burst.className = 'cart-milestone-confetti';
    burst.setAttribute('aria-hidden', 'true');
    const drawer = this.closest('.cart-drawer__dialog');
    const animationHost = drawer instanceof HTMLElement ? drawer : document.body;
    const hostBounds = animationHost.getBoundingClientRect();
    const rewardBounds = this.getBoundingClientRect();
    const originX = rewardBounds.width ? rewardBounds.left + rewardBounds.width / 2 - hostBounds.left : hostBounds.width / 2;
    const originY = rewardBounds.height ? rewardBounds.bottom - hostBounds.top : hostBounds.height * 0.3;
    Object.assign(burst.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '20',
      overflow: 'hidden',
      pointerEvents: 'none',
    });
    const animations = [];

    for (let index = 0; index < 64; index += 1) {
      const piece = document.createElement('span');
      const angle = (Math.PI * 2 * index) / 64 + Math.random() * 0.2;
      const distance = 150 + Math.random() * 320;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance + 190;
      const rotation = Math.round(Math.random() * 900 - 450);
      const delay = Math.round(Math.random() * 120);
      Object.assign(piece.style, {
        position: 'absolute',
        top: `${originY}px`,
        left: `${originX}px`,
        width: '9px',
        height: '14px',
        borderRadius: '2px',
        backgroundColor: colors[index % colors.length],
      });
      burst.append(piece);
      animations.push(
        piece.animate(
          [
            { opacity: 0, transform: 'translate(-50%, -50%) scale(.4) rotate(0deg)' },
            { opacity: 1, offset: 0.08 },
            {
              opacity: 0,
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(1) rotate(${rotation}deg)`,
            },
          ],
          { duration: 1800, delay, easing: 'cubic-bezier(.2,.7,.25,1)', fill: 'both' }
        )
      );
    }

    animationHost.append(burst);
    Promise.allSettled(animations.map((animation) => animation.finished)).then(() => burst.remove());
  }

  #queueSync(cartItems) {
    if (Array.isArray(cartItems)) this.#queuedCartItems = cartItems;
    if (this.#syncQueued) return;
    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      const queuedCartItems = this.#queuedCartItems;
      this.#queuedCartItems = null;
      this.syncGift(queuedCartItems);
    });
  }

  async syncGift(cartItems = null) {
    if (window.__cartMilestoneGiftSyncing) {
      window.__cartMilestoneGiftSyncPending = true;
      if (Array.isArray(cartItems)) window.__cartMilestoneGiftPendingItems = cartItems;
      return;
    }

    const unlockedGifts = [...this.querySelectorAll('[data-cart-milestone-reward]')]
      .filter(
        (reward) =>
          reward instanceof HTMLElement &&
          reward.dataset.reached === 'true' &&
          reward.dataset.rewardType === 'free_gift' &&
          Number(reward.dataset.giftVariantId) > 0
      )
      .sort((a, b) => Number(b.dataset.level) - Number(a.dataset.level));

    const desiredReward = unlockedGifts[0];
    const desiredVariantId = desiredReward instanceof HTMLElement ? Number(desiredReward.dataset.giftVariantId) : 0;
    const desiredLevel = desiredReward instanceof HTMLElement ? desiredReward.dataset.level : '';

    window.__cartMilestoneGiftSyncing = true;
    try {
      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((element) => element.dataset.sectionId)
            .filter(Boolean)
        ),
      ];
      let currentCartItems = cartItems;
      if (!Array.isArray(currentCartItems)) {
        const cartUrl = new URL(`${window.Shopify.routes.root}cart.js`, window.location.origin);
        cartUrl.searchParams.set('_milestone_sync', Date.now().toString());
        const cartResponse = await fetch(cartUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();
        currentCartItems = cart.items;
      }

      // A configured gift variant must never survive as a normal, untagged line.
      // Otherwise Shopify's product discount can make both that line and the
      // automatically-added gift free.
      const configuredGiftVariantIds = new Set(
        [...this.querySelectorAll('[data-cart-milestone-reward]')]
          .map((reward) => Number(reward.dataset.giftVariantId))
          .filter((variantId) => variantId > 0)
      );
      const milestoneGiftItems = currentCartItems.filter((item) => item.properties?._cart_milestone_gift);
      const bundleLinesById = new Map();
      currentCartItems
        .filter((item) => item.properties?._bundle_id && !item.properties?._bundle_free_gift)
        .forEach((item) => {
          const bundleId = String(item.properties._bundle_id);
          bundleLinesById.set(bundleId, [...(bundleLinesById.get(bundleId) || []), item]);
        });
      const validBundleGifts = currentCartItems.filter((item) => {
        if (!item.properties?._bundle_free_gift) return false;
        const bundleId = String(item.properties?._bundle_id || '');
        const bundleLines = bundleLinesById.get(bundleId) || [];
        const usesLinkedPrimary = bundleLines.some((line) => String(line.properties?._bundle_schema) === '2');
        const hasQualifyingLine = usesLinkedPrimary
          ? bundleLines.some((line) => line.properties?._bundle_primary)
          : bundleLines.length > 0;
        return Boolean(bundleId && hasQualifyingLine);
      });
      let cartItemCount = currentCartItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
      const desiredGift = milestoneGiftItems.find(
        (item) => item.variant_id === desiredVariantId && String(item.properties._cart_milestone_gift) === desiredLevel
      );
      // Keep one gift in the entire cart. An unlocked milestone gift takes
      // priority; otherwise retain only the first valid bundle gift. Including
      // configured variant IDs catches manual or stale untagged gift lines too.
      const retainedGift = desiredGift || (!desiredVariantId ? validBundleGifts[0] : null);
      const giftCandidates = currentCartItems.filter(
        (item) =>
          item.properties?._cart_milestone_gift ||
          item.properties?._bundle_free_gift ||
          configuredGiftVariantIds.has(Number(item.variant_id))
      );
      const updates = Object.fromEntries(
        giftCandidates.filter((item) => item !== retainedGift).map((item) => [item.key, 0])
      );
      if (retainedGift && Number(retainedGift.quantity) !== 1) updates[retainedGift.key] = 1;

      if (Object.keys(updates).length) {
        const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!updateResponse.ok) throw new Error('Could not update milestone gifts.');
        const updatedCart = await updateResponse.json();
        cartItemCount = Number(updatedCart.item_count || 0);
      }

      if (desiredVariantId && !desiredGift) {
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: desiredVariantId,
                quantity: 1,
                properties: { _cart_milestone_gift: desiredLevel, _free_gift: 'true' },
              },
            ],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });
        if (!addResponse.ok) throw new Error('Could not add the milestone gift.');
        const addedItems = await addResponse.json();
        cartItemCount += 1;
      }

      if (Object.keys(updates).length || (desiredVariantId && !desiredGift)) {
        await Promise.all(
          sectionIds.map((sectionId) => sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' }))
        );
        document.querySelectorAll('cart-icon').forEach((cartIcon) => cartIcon.renderCartBubble?.(cartItemCount, false));
      }
    } catch (error) {
      console.warn('[cart-milestone-rewards] Gift sync failed:', error);
    } finally {
      window.__cartMilestoneGiftSyncing = false;
      if (window.__cartMilestoneGiftSyncPending) {
        window.__cartMilestoneGiftSyncPending = false;
        const pendingCartItems = window.__cartMilestoneGiftPendingItems;
        window.__cartMilestoneGiftPendingItems = null;
        const currentRewards = document.querySelector('cart-milestone-rewards');
        if (currentRewards instanceof CartMilestoneRewards) {
          queueMicrotask(() => currentRewards.syncGift(pendingCartItems));
        }
      }
    }
  }
}

// Capture the cart response before cart-items replaces the current section. This
// guarantees that milestone transitions are observed even when the old custom
// element is disconnected during the update event.
document.addEventListener(
  ThemeEvents.cartUpdate,
  (event) => {
    const sections = event.detail?.data?.sections;
    if (!sections || typeof sections !== 'object') return;

    const sectionHtml = Object.values(sections).find(
      (html) => typeof html === 'string' && html.includes('data-cart-milestone-reward')
    );
    if (typeof sectionHtml !== 'string') return;

    const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const reachedLevels = new Set(
      [...sectionDocument.querySelectorAll('[data-cart-milestone-reward][data-reached="true"]')].map(
        (reward) => reward.getAttribute('data-level')
      )
    );
    const previousLevels = window.__cartMilestoneReachedLevels;
    window.__cartMilestoneReachedLevels = reachedLevels;
    if (!(previousLevels instanceof Set)) return;

    const newlyReached = [...reachedLevels].some((level) => !previousLevels.has(level));
    if (!newlyReached || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    queueMicrotask(() => {
      const updatedRewards = document.querySelector('cart-milestone-rewards');
      if (updatedRewards instanceof CartMilestoneRewards) updatedRewards.celebrate();
    });
  },
  { capture: true }
);

if (!customElements.get('cart-milestone-rewards')) {
  customElements.define('cart-milestone-rewards', CartMilestoneRewards);
}
